import { PhoneNumberUtil } from 'google-libphonenumber'
import { useState } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dimensions, Keyboard, StyleProp, View, ViewStyle } from 'react-native'
import { Button, Modal, Portal, Searchbar, TextInput } from 'react-native-paper'
import { useTheme } from '../../../../hooks/theme.hook'
import { ControlledTextInput } from '../../../../@ui/form/controlled-text.input'
import { CountryList } from '../../../../@ui/form/country-list'
import * as Sentry from '@sentry/react-native'

const phoneUtil = PhoneNumberUtil.getInstance()

interface ControlledMobileInputProps<A extends string, B extends string> {
  disabled?: boolean
  control: any
  getValues: (payload?: string | string[]) => Object
  trigger: (name?: (A | B) | (A | B)[]) => Promise<boolean>
  names: [A, B]
  rules?: any
  error?: any
  helperText?: string
  wrapperStyle?: StyleProp<ViewStyle>
  onIsValid?: (isValid: boolean) => void
}

function closePicker() {
  Keyboard.dismiss()
}

export function ControlledMobileInput<A extends string, B extends string>(
  props: ControlledMobileInputProps<A, B>
) {
  const theme = useTheme()
  const { t, i18n } = useTranslation('login_register_reset')
  const {
    control,
    error,
    helperText,
    names,
    wrapperStyle,
    disabled,
    getValues,
    trigger,
  } = props
  const [searchQuery, setSearchQuery] = useState('')
  const [showCountryPicker, setShowCountryPicker] = useState(false)
  const modalHeight = Dimensions.get('window').height * 0.6

  return (
    <View style={wrapperStyle}>
      <Controller
        control={control}
        name={names[0]}
        render={({ field: { onChange, value } }) => (
          <View
            style={[
              {
                paddingVertical: theme.spacing(2),
                position: 'relative',
                flex: 0,
                flexShrink: 0,
                flexGrow: 0,
                flexBasis: '20%',
                minWidth: 100,
                marginRight: theme.spacing(2),
              },
            ]}
          >
            <TextInput
              disabled={disabled}
              style={{ top: -2 }}
              value={value}
              autoComplete="tel-country-code"
              editable={false}
              onTouchStart={
                !disabled
                  ? () => {
                      setShowCountryPicker(true)
                    }
                  : undefined
              }
              right={
                <TextInput.Icon
                  icon={showCountryPicker ? 'menu-up' : 'menu-down'}
                  disabled={disabled}
                  onPress={() => {
                    setShowCountryPicker(!showCountryPicker)
                  }}
                />
              }
              dense
              mode="outlined"
            />
            <Portal>
              <Modal
                visible={showCountryPicker}
                onDismiss={() => setShowCountryPicker(false)}
                contentContainerStyle={{
                  backgroundColor: theme.colors.surface,
                  margin: theme.spacing(2),
                  padding: theme.spacing(2),
                  height: modalHeight,
                }}
              >
                <View style={{ flex: 1 }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      marginBottom: theme.spacing(1),
                    }}
                  >
                    <Button
                      mode="text"
                      compact
                      onPress={() => {
                        closePicker()
                        setShowCountryPicker(false)
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  </View>
                  <Searchbar
                    placeholder={t('country-code-placeholder')}
                    onChangeText={setSearchQuery}
                    value={searchQuery}
                  />
                  <CountryList
                    lang={i18n.language.split('-')[0]}
                    style={{ flex: 1 }}
                    searchValue={searchQuery}
                    pickerButtonOnPress={(item) => {
                      onChange(item.dial_code)
                      closePicker()
                      setShowCountryPicker(false)
                      trigger(names[1]).then()
                    }}
                  />
                </View>
              </Modal>
            </Portal>
          </View>
        )}
      />
      <ControlledTextInput
        disabled={disabled}
        control={control}
        name={names[1]}
        keyboardType={'number-pad'}
        autoComplete="tel-national"
        autoCapitalize="none"
        autoCorrect={false}
        rules={{
          required: t('requiredMobile'),
          validate: (value: string) => {
            let isValid = false
            const dialCode = getValues(names[0]) as string
            const region = phoneUtil.getRegionCodeForCountryCode(
              parseInt(dialCode)
            )
            try {
              const phoneNumber = phoneUtil.parse(value, region)
              isValid = phoneUtil.isValidNumber(phoneNumber)
            } catch (e) {
              console.error(e)
              Sentry.captureException(e)
            }
            props.onIsValid?.(isValid)
            return isValid || t('invalidMobile')
          },
        }}
        placeholder={t('MobileNumber')}
        label={t('MobileNumber')}
        mode="outlined"
        error={error}
        helperText={helperText}
        wrapperStyle={[{ flex: 1 }]}
      />
    </View>
  )
}
